import type { WidgetConfigElement } from '~/types/widget';

/**
 * Which Pulse control the inspector renders for an editable attribute.
 * `spacing` currently falls back to a text input (e.g. "8px 16px"); kept as a
 * distinct kind so a richer 4-side control can replace it later without touching the catalog.
 */
export type ControlKind =
  | 'text'
  | 'textarea'
  | 'number'
  | 'color'
  | 'select'
  | 'toggle'
  | 'font'
  | 'spacing'
  | 'image'
  | 'json'
  /** A single CSS length: number field plus a unit picker, so nobody types "12px" by hand. */
  | 'length';

export interface ControlOption {
  label: string;
  value: string;
}

/**
 * Whether a control edits a value shared across every claim-portal page (`global` — branding/style)
 * or one that is authored per page (`page` — the copy a merchant writes for a specific screen).
 * Drives the designer's persist-toggle scope hint. Undefined behaves as `page` (page-local content).
 */
export type ControlScope = 'global' | 'page';

/** Describes one editable attribute of an element, and how to render its control. */
export interface ControlSchema {
  /** Key into WidgetConfigElement.attributes (verbatim, e.g. "border-radius"). */
  attr: string;
  label: string;
  kind: ControlKind;
  /** Inspector section heading the control is grouped under. */
  group?: string;
  /** Options for the `select` kind. */
  options?: ControlOption[];
  placeholder?: string;
  /** Cross-page scope of the edited value (claim-portal designer). */
  scope?: ControlScope;
  /**
   * Which node the value is written to. `parent` edits the containing element — how an element
   * sits inside its group is a property of the group, but a merchant looks for it on the element
   * they selected. The control is hidden when the node has no parent, or when the parent's type
   * is not in `parentTypes`.
   */
  target?: 'self' | 'parent';
  parentTypes?: string[];
  /**
   * Marks a control whose meaning flips with its container's `flex-direction`. `align-items` is the
   * CROSS axis and `justify-content` the MAIN one, so on a row the first is vertical and the second
   * horizontal — and on a column they swap. A fixed label is therefore wrong half the time, which
   * became reachable the moment Direction started offering Stacked. {@link resolveAxisControl}
   * rewrites the label and the option wording from the container's actual direction.
   */
  axis?: 'main' | 'cross';
  /**
   * Restricts a parent-targeted control to a container laying out in this direction. "Position in
   * row" and "Position in stack" write different attributes for the same intent, so which one is
   * offered follows the container's real direction rather than only its type.
   */
  parentDirection?: 'row' | 'column';
  /** Values written for a `toggle` kind; defaults to the booleans themselves. */
  onValue?: string;
  offValue?: string;
  /** Units offered by the `length` kind. Defaults to px/%/em/rem. */
  units?: string[];
  /**
   * `length` kind: offer a unit-free option. Needed by anything CSS accepts bare — `line-height:
   * 1.5` is a multiplier, and appending a unit to it changes the meaning. The option also appears
   * unconditionally when the current value is already unit-free, so no existing value can be
   * silently coerced.
   */
  unitless?: boolean;
  /**
   * `length` kind: CSS keywords this attribute accepts instead of a measurement — `normal` for
   * letter-spacing and line-height, `auto` for width. Offered in the unit picker so a keyword is
   * something a merchant chooses rather than a word they have to know and type.
   */
  keywords?: string[];
  /** One short sentence under the control, for anything the label cannot carry alone. */
  help?: string;
  /**
   * Show this control only while another attribute on the same element is truthy. Keeps controls
   * that would silently do nothing off the panel — an image colour means nothing until the file is
   * being drawn as inline SVG.
   */
  showWhen?: string;
  /**
   * Show this control only while another attribute matches this pattern. Keeps a control off the
   * panel where turning it on would produce nonsense — `isSvg` on a PNG makes the runtime inline
   * the file's bytes as markup, which renders as garbage.
   */
  showWhenMatches?: { attr: string; pattern: string };
}

export type ElementCategory = 'layout' | 'content' | 'control' | 'media' | 'advanced';

/**
 * Editor metadata for a single widget element type. Mirrors the runtime `attrs`
 * declared on the matching cart-widget component (see the `mirrors` comment on each
 * entry in cart-catalog.ts). This is editor-only UI metadata — it never ships to the storefront.
 */
export interface ElementSchema {
  /** Matches the cart-widget `Renderer.elementMap` key, e.g. "vertical-stack". */
  type: string;
  displayName: string;
  /** Pulse icon component name; resolved through builder/catalog/icons.ts. */
  icon: string;
  category: ElementCategory;
  canHaveChildren: boolean;
  /** undefined = any catalog type may nest here; [] = no children allowed. */
  allowedChildTypes?: string[];
  /** Seed attributes for a node created from the palette (full default set). */
  defaultAttributes: Record<string, string | number | boolean>;
  /** Seed children for a node created from the palette. */
  defaultChildren?: WidgetConfigElement[];
  /** Attributes exposed as inspector controls (a curated subset of defaultAttributes). */
  controls: ControlSchema[];
  /** Read-only event hints shown in the inspector. */
  emitsHints?: string[];
  listensHints?: string[];
  /** false = structural/contextual only, hidden from the palette's add list. */
  paletteVisible?: boolean;
  /**
   * Types this element can be turned into in place, keeping its wording, styling and `emits`.
   * Offered as "Element type" in the inspector — a decline link becoming a filled button, say.
   */
  convertsTo?: string[];
  /**
   * Renders this container in the Layers list as a plain-English grouping band rather than a
   * named element row. Layout scaffolding a merchant should not have to reason about: they see
   * the elements it groups and a label saying how they sit, not "Horizontal Stack".
   */
  groupBandLabel?: string;
  /** One sentence shown at the top of the inspector, for behaviour the controls cannot show. */
  notice?: string;
  /**
   * A fixed part of its parent: the Layers list offers no drag handle, duplicate or delete for it.
   * The tiles of the coverage selector are addressed one by one but the set is the component's.
   */
  locked?: boolean;
}

export type CatalogMap = Record<string, ElementSchema>;

/** Font family choices for the `font` control kind. */
export const FONT_OPTIONS: ControlOption[] = [
  { label: 'Inter', value: 'Inter' },
  { label: 'Arial', value: 'Arial' },
  { label: 'Helvetica', value: 'Helvetica' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Roboto', value: 'Roboto' },
  { label: 'Open Sans', value: 'Open Sans' },
  { label: 'Lato', value: 'Lato' },
  { label: 'Montserrat', value: 'Montserrat' },
  { label: 'Poppins', value: 'Poppins' },
];
